import { ROLE } from '@maxpreps/shared/constants/authentication';
import { pseudoSchoolRedirectUrl } from '@maxpreps/shared/constants/pseudoSchools';
import useRequestContext from '@maxpreps/shared/contexts/RequestContext';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import { Mobile } from '@maxpreps/shared/corecomponents/ScreenSize/ScreenSize';
import {
  getContestTypeText,
  getHomeAwayText
} from '@maxpreps/shared/functions/utils/contestUtils';
import { getFormattedAddress } from '@maxpreps/shared/functions/utils/getFormattedAddress';
import { getIsPseudoSchool } from '@maxpreps/shared/functions/utils/schoolUtils';
import { hasPermission } from '@maxpreps/shared/functions/utils/userUtils';
import useIsAdmin from '@maxpreps/shared/hooks/useIsAdmin';
import { useIsMobile } from '@maxpreps/shared/hooks/useIsScreenSize';
import { useUserRoles } from '@maxpreps/shared/redux/user/userHooks';
import THEME from '@maxpreps/shared/styles/colors';
import Font from '@maxpreps/shared/styles/font';
import StyledText from '@maxpreps/shared/ui/Text/StyledText';
import React from 'react';
import styled from 'styled-components';
import getCSRRewriteAttributes from '../../../utils/getCSRRewriteAttributes';
import TeamMainCell from '../../shared/TeamMainCell';
import { getTeamUrl } from './../../../utils/navigation';
import { Page } from './../../../utils/pages';
import ScheduleMascot from './ScheduleMascot';

// Designed for a min 320px width device. Any larger and FGB is off the page.
const MAX_OPPONENT_NAME_WIDTH = 14;

const getOpponentHref = (
  url,
  schoolId,
  ssid,
  allSeasonId,
  linkToOpponentScheduleAdmin,
  appType
) => {
  if (!url) return null;

  let modifiedUrl = url;

  if (linkToOpponentScheduleAdmin) {
    const queryString: { admin?: number } = {};

    if (url.indexOf('admin=1') === -1) {
      queryString.admin = 1;
    }

    modifiedUrl = getTeamUrl(Page.Schedule, modifiedUrl, queryString);
  }

  return getCSRRewriteAttributes(
    modifiedUrl,
    schoolId, // oponentSchoolId,
    ssid, // sportSeasonId
    allSeasonId,
    appType
  );
};

const getMainTextProps = (
  contest,
  allSeasonId,
  isMobile,
  isAdmin,
  roles,
  appType
) => {
  const {
    name,
    sportSeasonId,
    teamId,
    isTeamTBA,
    teamCanonicalUrl,
    schoolNameAcronym
  } = contest.opponentTeam;

  const opponentTeamNameDisplay =
    name?.length > MAX_OPPONENT_NAME_WIDTH && isMobile
      ? schoolNameAcronym
      : name;

  const isStandardAdminOnAdminPage =
    isAdmin && hasPermission([{ roleId: ROLE.standardAdmin }], roles);

  // don't link TBA opponents
  if (isTeamTBA) {
    return { mainText: 'Opponent TBA' };
  }

  // special handling for psuedo schools
  if (getIsPseudoSchool(teamId || '')) {
    return {
      mainHref: pseudoSchoolRedirectUrl,
      mainText: name
    };
  }

  // get the canonical url
  const anchorAttributes = getOpponentHref(
    teamCanonicalUrl,
    teamId,
    sportSeasonId,
    allSeasonId,
    isStandardAdminOnAdminPage,
    appType
  );

  return {
    mainHref: anchorAttributes?.href,
    mainAs: anchorAttributes?.as,
    mainText: opponentTeamNameDisplay
  };
};

const getSubText = (contest, stateData, isMobile) => {
  const { contestType, homeAwayType } = contest.currentTeam;

  return (
    <StyledText
      as="div"
      className={isMobile ? Font.f12_tall : Font.f14_tall}
      $color={THEME.text.tertiary}
    >
      {getHomeAwayText(homeAwayType)}
      {' • '}
      {getContestTypeText(contestType, stateData?.leagueAlias)}
    </StyledText>
  );
};

const StyledContestOpponent = styled.div`
  display: flex;

  & .photo-or-initial {
    margin: 0 8px 0 0;
  }
`;

const ContestSummary = ({ contest }) => {
  const teamContext = useTeamContext();
  const isMobile = useIsMobile();
  const isAdmin = useIsAdmin();
  const roles = useUserRoles();
  const { appType } = useRequestContext();

  const { stateCode, allSeasonId } = teamContext.data;
  const { name, state, city } = contest.opponentTeam;

  const stateText = state === stateCode ? undefined : state;

  const cityState = getFormattedAddress(undefined, city, stateText, undefined, {
    schoolName: name
  });

  const mainTextProps = getMainTextProps(
    contest,
    allSeasonId,
    isMobile,
    isAdmin,
    roles,
    appType
  );

  const subText = getSubText(contest, teamContext.stateData, isMobile);

  return (
    <StyledContestOpponent>
      <Mobile>
        <ScheduleMascot contest={contest} />
      </Mobile>
      <TeamMainCell
        subText={subText}
        badges={cityState ? [cityState] : undefined}
        mainColor={THEME.text.primary}
        {...mainTextProps}
      />
    </StyledContestOpponent>
  );
};

export default ContestSummary;
